import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";
import { getPersistedAccessToken } from "../auth/authClient";

export type DashboardInsight = {
  key: string;
  label: string;
  value: string;
  color: string;
  icon: string;
  description: string;
};

export type GoalMonthCount = {
  month: number;
  year: number;
  count: number;
  titles: string[];
  allCompleted?: boolean;
};

export type GoalCardSummary = {
  id: string;
  title: string;
  completed: boolean;
  totalMilestones: number;
  completedMilestones: number;
};

export type DreamCard = {
  freeEditable?: boolean;
  id: string;
  title: string;
  description: string;
  category: string;
  color: string;
  startMonth: number;
  startYear: number;
  targetMonth: number;
  targetYear: number;
  completedPercent: number;
  totalGoals: number;
  completedGoals: number;
  totalMilestones: number;
  completedMilestones: number;
  goals: GoalCardSummary[];
};

export type DashboardTimelineDream = {
  id: string;
  title: string;
  color: string;
  category: string;
  startMonth: number;
  startYear: number;
  targetMonth: number;
  targetYear: number;
  completedPercent: number;
  goalsByMonth: GoalMonthCount[];
};

type DashboardState = {
  dreams: Record<string, unknown>[];
  goals: Record<string, unknown>[];
  milestones: {
    id: string;
    goalId?: string;
    date?: string;
    [key: string]: unknown;
  }[];
  loading: boolean;
  saveMilestoneBatchDateLoading: boolean;
  error: string | null;
  insights: DashboardInsight[];
  insightsLoading: boolean;
  timeline: DashboardTimelineDream[];
  timelineLoading: boolean;
  dreamCards: DreamCard[];
  dreamCardsLoading: boolean;
};

const initialState: DashboardState = {
  dreams: [],
  goals: [],
  milestones: [],
  loading: false,
  saveMilestoneBatchDateLoading: false,
  error: null,
  insights: [],
  insightsLoading: false,
  timeline: [],
  timelineLoading: false,
  dreamCards: [],
  dreamCardsLoading: false,
};

type SaveMilestoneBatchDateArgs = {
  updates: {
    dreamId: string;
    goalId: string;
    milestoneId: string;
    date: string;
  }[];
};

export const fetchTrackFull = createAsyncThunk(
  "dashboard/fetchTrackFull",
  async (_, { rejectWithValue }) => {
    try {
      const response = await request(API_URLS.trackFull, {
        method: "GET",
        auth: true,
      });
      return {
        dreams: Array.isArray(response?.dreams) ? response.dreams : [],
        goals: Array.isArray(response?.goals) ? response.goals : [],
        milestones: Array.isArray(response?.milestones) ? response.milestones : [],
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to load dashboard data";
      return rejectWithValue(message);
    }
  },
);

export const fetchDreamCards = createAsyncThunk(
  "dashboard/fetchDreamCards",
  async (_, { rejectWithValue }) => {
    try {
      const data = await request(API_URLS.dashboardDreams, { method: "GET", auth: true });
      return Array.isArray(data) ? data : [];
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to load dream cards");
    }
  },
);

export const fetchDashboardInsights = createAsyncThunk(
  "dashboard/fetchDashboardInsights",
  async (_, { rejectWithValue }) => {
    try {
      const data = await request(API_URLS.dashboardInsights, { method: "GET", auth: true });
      return Array.isArray(data) ? data : [];
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to load insights");
    }
  },
);

export const fetchDashboardTimeline = createAsyncThunk(
  "dashboard/fetchDashboardTimeline",
  async (_, { rejectWithValue }) => {
    try {
      const data = await request(API_URLS.dashboardTimeline, { method: "GET", auth: true });
      return Array.isArray(data) ? data : [];
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : "Failed to load timeline");
    }
  },
);

export const saveMilestoneBatchDate = createAsyncThunk(
  "dashboard/saveMilestoneBatchDate",
  async ({ updates }: SaveMilestoneBatchDateArgs, { rejectWithValue }) => {
    try {
      const accessToken = getPersistedAccessToken();
      if (!accessToken) {
        return rejectWithValue("Missing access token");
      }

      await request(API_URLS.milestoneBatchDate, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: { updates },
      });

      return updates;
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to save milestone dates";
      return rejectWithValue(message);
    }
  },
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrackFull.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrackFull.fulfilled, (state, action) => {
        state.loading = false;
        state.dreams = action.payload.dreams;
        state.goals = action.payload.goals;
        state.milestones = action.payload.milestones;
      })
      .addCase(fetchTrackFull.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load dashboard data";
      })
      .addCase(saveMilestoneBatchDate.pending, (state) => {
        state.saveMilestoneBatchDateLoading = true;
      })
      .addCase(saveMilestoneBatchDate.fulfilled, (state, action) => {
        state.saveMilestoneBatchDateLoading = false;
        const updates = action.payload || [];
        const dateByMilestoneLocation: Record<string, string> = {};
        for (const update of updates) {
          const locationKey = `${update.goalId}::${update.milestoneId}`;
          dateByMilestoneLocation[locationKey] = update.date;
        }
        state.milestones = state.milestones.map((milestone) => {
          const milestoneGoalId = String(milestone.goalId || "");
          const locationKey = `${milestoneGoalId}::${milestone.id}`;
          const nextDate = dateByMilestoneLocation[locationKey];
          if (nextDate === undefined) return milestone;
          return {
            ...milestone,
            date: nextDate,
          };
        });
      })
      .addCase(saveMilestoneBatchDate.rejected, (state) => {
        state.saveMilestoneBatchDateLoading = false;
      })
      .addCase(fetchDashboardInsights.pending, (state) => {
        state.insightsLoading = true;
      })
      .addCase(fetchDashboardInsights.fulfilled, (state, action) => {
        state.insightsLoading = false;
        state.insights = action.payload;
      })
      .addCase(fetchDashboardInsights.rejected, (state) => {
        state.insightsLoading = false;
      })
      .addCase(fetchDashboardTimeline.pending, (state) => {
        state.timelineLoading = true;
      })
      .addCase(fetchDashboardTimeline.fulfilled, (state, action) => {
        state.timelineLoading = false;
        state.timeline = action.payload;
      })
      .addCase(fetchDashboardTimeline.rejected, (state) => {
        state.timelineLoading = false;
      })
      .addCase(fetchDreamCards.pending, (state) => {
        state.dreamCardsLoading = true;
      })
      .addCase(fetchDreamCards.fulfilled, (state, action) => {
        state.dreamCardsLoading = false;
        state.dreamCards = action.payload;
      })
      .addCase(fetchDreamCards.rejected, (state) => {
        state.dreamCardsLoading = false;
      });
  },
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;

